/**
 * WHEN THE WORK CAN HAPPEN (spec §62).
 *
 * The Gantt chart is a picture OF this engine; it is not the schedule. Given
 * the tasks, how long each takes, what each one waits for, and what has
 * actually happened so far, this computes:
 *
 *   • the earliest each task can start and finish;
 *   • the latest each could start and finish without delaying the project;
 *   • how much slack that leaves — the float;
 *   • which tasks have none, and therefore decide the finish date;
 *   • what can be started today, and what is waiting on what.
 *
 * It is a plain forward/backward pass over the dependency graph — real
 * critical-path method, not "the longest-looking bar". A task is critical
 * because delaying it delays the calculated completion date.
 *
 * PRINCIPLES
 *   • Pure: dates in, dates out. No React, no database, no clock unless one
 *     is passed in (tests never depend on wall-clock time).
 *   • Honest: a cycle is reported, never scheduled around. A pinned date
 *     that cannot be met is reported, never quietly moved. Actual dates beat
 *     planned ones, because they happened.
 *   • Plain-language first: the terms the user sees are "ready", "waiting
 *     on" and "days of flexibility"; float, CPM and predecessors stay in
 *     here where they belong.
 */
import {
  dayAfter,
  dayBefore,
  describeDuration,
  finishOf,
  isIsoDate,
  nextWorkingDay,
  startFrom,
  workingDaysBetween,
  type ScheduleCalendar,
} from "./workdays";

export type { ScheduleCalendar };

/** What the user set. Ready and Blocked are DERIVED, never stored. */
export type TaskStatus = "not-started" | "in-progress" | "complete";

/** What the schedule says about it right now. */
export type TaskState = "complete" | "in-progress" | "ready" | "blocked" | "scheduled";

export interface ScheduleTaskInput {
  id: string;
  name: string;
  /** Whole working days; 0 means a milestone — a moment, not work. */
  durationDays: number;
  status: TaskStatus;
  /** A date the user pinned this task to, if any. */
  plannedStart: string | null;
  actualStart: string | null;
  actualFinish: string | null;
  /** Ids of the tasks that must FINISH before this one can start. */
  dependsOn: readonly string[];
  sortOrder?: number;
}

export interface ScheduleOptions {
  /** The day the project's work begins. */
  startDate: string;
  calendar: ScheduleCalendar;
  /** Injected so tests never depend on the clock. */
  today?: string;
}

export interface ScheduledTask {
  id: string;
  name: string;
  durationDays: number;
  milestone: boolean;
  status: TaskStatus;
  state: TaskState;
  /** When it is expected to run — actual dates where they exist. */
  start: string;
  finish: string;
  /** The latest it could run without moving the completion date. */
  latestStart: string;
  latestFinish: string;
  /** Working days of slack. 0 = delaying this delays the project. */
  totalFloatDays: number;
  critical: boolean;
  /** Prerequisites that are not finished yet, in the order given. */
  blockedBy: string[];
  /** Tasks that wait directly on this one. */
  unlocks: string[];
  /** Everything that ultimately waits on it — how much it holds up. */
  downstreamCount: number;
  /** True when the dates came from what happened, not from the plan. */
  fromActuals: boolean;
}

export type ScheduleProblemKind = "cycle" | "pinned-too-early" | "invalid-date";

export interface ScheduleProblem {
  kind: ScheduleProblemKind;
  taskIds: string[];
  message: string;
}

export interface Schedule {
  tasks: ScheduledTask[];
  byId: ReadonlyMap<string, ScheduledTask>;
  /** The projected completion date, or null when there is nothing to do. */
  projectFinish: string | null;
  problems: ScheduleProblem[];
  calendar: ScheduleCalendar;
  startDate: string;
}

/* ------------------------------------------------------------------ *
 * Graph
 * ------------------------------------------------------------------ */

/** Prerequisite ids that actually exist, de-duplicated, self-links dropped. */
function edgesOf(tasks: readonly ScheduleTaskInput[]): Map<string, string[]> {
  const known = new Set(tasks.map((t) => t.id));
  const out = new Map<string, string[]>();
  for (const task of tasks) {
    const seen = new Set<string>();
    const deps: string[] = [];
    for (const id of task.dependsOn) {
      if (id === task.id || !known.has(id) || seen.has(id)) continue;
      seen.add(id);
      deps.push(id);
    }
    out.set(task.id, deps);
  }
  return out;
}

/**
 * Depth-first search for a cycle. Returns the ids on the loop, in order, so
 * the user can be told which tasks are waiting on each other rather than
 * simply that "something" is wrong.
 */
export function findCycle(tasks: readonly ScheduleTaskInput[]): string[] | null {
  const edges = edgesOf(tasks);
  const state = new Map<string, 0 | 1 | 2>();
  const stack: string[] = [];

  const visit = (id: string): string[] | null => {
    const mark = state.get(id) ?? 0;
    if (mark === 1) {
      const from = stack.indexOf(id);
      return stack.slice(from).concat(id);
    }
    if (mark === 2) return null;
    state.set(id, 1);
    stack.push(id);
    for (const next of edges.get(id) ?? []) {
      const found = visit(next);
      if (found) return found;
    }
    stack.pop();
    state.set(id, 2);
    return null;
  };

  for (const task of tasks) {
    const found = visit(task.id);
    if (found) return found;
  }
  return null;
}

/** Would adding "task depends on candidate" create a loop? */
export function wouldCreateCycle(
  tasks: readonly ScheduleTaskInput[],
  taskId: string,
  dependsOnId: string,
): boolean {
  if (taskId === dependsOnId) return true;
  const proposed = tasks.map((t) =>
    t.id === taskId ? { ...t, dependsOn: [...t.dependsOn, dependsOnId] } : t,
  );
  return findCycle(proposed) !== null;
}

/** Tasks in an order where every prerequisite comes first. Null on a cycle. */
function topologicalOrder(tasks: readonly ScheduleTaskInput[]): ScheduleTaskInput[] | null {
  const edges = edgesOf(tasks);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const state = new Map<string, 0 | 1 | 2>();
  const order: ScheduleTaskInput[] = [];
  let cyclic = false;

  const visit = (id: string): void => {
    const mark = state.get(id) ?? 0;
    if (mark === 1) {
      cyclic = true;
      return;
    }
    if (mark === 2) return;
    state.set(id, 1);
    for (const next of edges.get(id) ?? []) visit(next);
    state.set(id, 2);
    const task = byId.get(id);
    if (task) order.push(task);
  };

  for (const task of tasks) visit(task.id);
  return cyclic ? null : order;
}

/* ------------------------------------------------------------------ *
 * The schedule
 * ------------------------------------------------------------------ */

export function computeSchedule(
  tasks: readonly ScheduleTaskInput[],
  options: ScheduleOptions,
): Schedule {
  const calendar = options.calendar;
  const problems: ScheduleProblem[] = [];
  const startDate = isIsoDate(options.startDate) ? nextWorkingDay(options.startDate, calendar) : null;
  if (!startDate) {
    return {
      tasks: [],
      byId: new Map(),
      projectFinish: null,
      problems: [
        { kind: "invalid-date", taskIds: [], message: "The project's schedule start date is not a date." },
      ],
      calendar,
      startDate: options.startDate,
    };
  }

  const cycle = findCycle(tasks);
  if (cycle) {
    const names = cycle.map((id) => tasks.find((t) => t.id === id)?.name ?? id);
    problems.push({
      kind: "cycle",
      taskIds: cycle,
      message: `These tasks are waiting on each other, so none of them could ever start: ${names.join(" → ")}.`,
    });
    return { tasks: [], byId: new Map(), projectFinish: null, problems, calendar, startDate };
  }

  const ordered = topologicalOrder(tasks)!;
  const edges = edgesOf(tasks);
  const byId = new Map(tasks.map((t) => [t.id, t]));

  // ---- successors, and how much each task ultimately holds up ----
  const successors = new Map<string, string[]>();
  for (const task of tasks) successors.set(task.id, []);
  for (const [id, deps] of edges) {
    for (const dep of deps) successors.get(dep)!.push(id);
  }
  const downstream = new Map<string, number>();
  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    const id = ordered[i].id;
    const seen = new Set<string>();
    const walk = (from: string): void => {
      for (const next of successors.get(from) ?? []) {
        if (seen.has(next)) continue;
        seen.add(next);
        walk(next);
      }
    };
    walk(id);
    downstream.set(id, seen.size);
  }

  // ---- forward pass: the earliest anything can happen ----
  const start = new Map<string, string>();
  const finish = new Map<string, string>();
  const fromActuals = new Map<string, boolean>();

  for (const task of ordered) {
    const deps = edges.get(task.id) ?? [];
    let earliest = startDate;
    for (const dep of deps) {
      const depFinish = finish.get(dep);
      if (!depFinish) continue;
      const after = dayAfter(depFinish, calendar);
      if (after > earliest) earliest = after;
    }

    // What happened beats what was planned.
    let actualStart: string | null = null;
    if (task.actualStart && isIsoDate(task.actualStart)) actualStart = task.actualStart;
    let taskStart = actualStart ?? earliest;

    if (!actualStart && task.plannedStart && isIsoDate(task.plannedStart)) {
      const pinned = nextWorkingDay(task.plannedStart, calendar);
      if (pinned < earliest) {
        problems.push({
          kind: "pinned-too-early",
          taskIds: [task.id],
          message: `${task.name} is set to start ${pinned}, but what it waits on cannot finish until ${dayBefore(earliest, calendar)}. The later date is used.`,
        });
        taskStart = earliest;
      } else {
        taskStart = pinned;
      }
    }

    taskStart = nextWorkingDay(taskStart, calendar);
    let taskFinish: string;
    if (task.actualFinish && isIsoDate(task.actualFinish)) {
      taskFinish = task.actualFinish;
      fromActuals.set(task.id, true);
    } else {
      taskFinish = finishOf(taskStart, Math.max(0, task.durationDays), calendar);
      fromActuals.set(task.id, actualStart !== null);
    }
    if (taskFinish < taskStart) taskFinish = taskStart;

    start.set(task.id, taskStart);
    finish.set(task.id, taskFinish);
  }

  const projectFinish = ordered.length === 0 ? null : [...finish.values()].sort().slice(-1)[0];

  // ---- backward pass: the latest anything could happen ----
  const latestFinish = new Map<string, string>();
  const latestStart = new Map<string, string>();
  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    const task = ordered[i];
    const nexts = successors.get(task.id) ?? [];
    let latest = projectFinish!;
    for (const next of nexts) {
      const nextLatestStart = latestStart.get(next);
      if (!nextLatestStart) continue;
      const before = dayBefore(nextLatestStart, calendar);
      if (before < latest) latest = before;
    }
    const duration = Math.max(0, task.durationDays);
    const ls = startFrom(latest, duration, calendar);
    latestFinish.set(task.id, latest);
    latestStart.set(task.id, ls);
  }

  // ---- what the user actually asked ----
  const today = options.today && isIsoDate(options.today) ? options.today : null;
  const result: ScheduledTask[] = tasks.map((task) => {
    const deps = edges.get(task.id) ?? [];
    const blockedBy = deps.filter((id) => byId.get(id)?.status !== "complete");
    const taskStart = start.get(task.id)!;
    const taskFinish = finish.get(task.id)!;
    const float = workingDaysBetween(taskStart, latestStart.get(task.id)!, calendar);

    // READY IS ABOUT PREREQUISITES, NOT ARITHMETIC (spec §62F). If the
    // work it waits on is finished, the work can start — whatever date the
    // plan happens to compute for it. The one exception is a date the USER
    // pinned in the future, which is them saying "not before this".
    const pinnedAhead =
      !!today && !!task.plannedStart && isIsoDate(task.plannedStart) && task.plannedStart > today;
    let state: TaskState;
    if (task.status === "complete") state = "complete";
    else if (task.status === "in-progress") state = "in-progress";
    else if (blockedBy.length > 0) state = "blocked";
    else if (pinnedAhead) state = "scheduled";
    else state = "ready";

    return {
      id: task.id,
      name: task.name,
      durationDays: Math.max(0, task.durationDays),
      milestone: task.durationDays <= 0,
      status: task.status,
      state,
      start: taskStart,
      finish: taskFinish,
      latestStart: latestStart.get(task.id)!,
      latestFinish: latestFinish.get(task.id)!,
      totalFloatDays: float,
      // A finished task cannot delay anything, whatever its float says.
      critical: task.status !== "complete" && float <= 0,
      blockedBy,
      unlocks: successors.get(task.id) ?? [],
      downstreamCount: downstream.get(task.id) ?? 0,
      fromActuals: fromActuals.get(task.id) ?? false,
    };
  });

  return {
    tasks: result,
    byId: new Map(result.map((t) => [t.id, t])),
    projectFinish,
    problems,
    calendar,
    startDate,
  };
}

/* ------------------------------------------------------------------ *
 * What should I do next (spec §62F)
 * ------------------------------------------------------------------ */

export interface PriorityItem {
  task: ScheduledTask;
  /** Why it is near the top, in the user's language. */
  reasons: string[];
  /** The names of what it directly unlocks, for "completing this unlocks…". */
  unlockNames: string[];
}

/**
 * Ready work, most schedule-sensitive first: on the critical path, then
 * holding up the most other work, then least slack. It is a recommendation
 * about the SCHEDULE — never a claim that this is the right way to build a
 * house.
 */
export function readyWork(schedule: Schedule, limit = 0): PriorityItem[] {
  const nameOf = (id: string): string => schedule.byId.get(id)?.name ?? "another task";
  const ready = schedule.tasks.filter((t) => t.state === "ready" || t.state === "in-progress");
  const ranked = [...ready].sort(
    (a, b) =>
      Number(b.critical) - Number(a.critical) ||
      b.downstreamCount - a.downstreamCount ||
      a.totalFloatDays - b.totalFloatDays ||
      a.start.localeCompare(b.start) ||
      a.name.localeCompare(b.name),
  );
  const items = ranked.map((task) => {
    const reasons: string[] = [];
    if (task.critical) reasons.push("Delaying this is expected to delay the whole project.");
    else if (task.totalFloatDays > 0) reasons.push(describeFloat(task.totalFloatDays));
    if (task.downstreamCount > 0) {
      reasons.push(
        `${task.downstreamCount} later task${task.downstreamCount === 1 ? "" : "s"} wait${task.downstreamCount === 1 ? "s" : ""} on it.`,
      );
    }
    if (task.state === "in-progress") reasons.push("Already started.");
    return { task, reasons, unlockNames: task.unlocks.map(nameOf) };
  });
  return limit > 0 ? items.slice(0, limit) : items;
}

/** Blocked work, with what it is actually waiting on. */
export function blockedWork(schedule: Schedule): Array<{ task: ScheduledTask; waitingOn: ScheduledTask[] }> {
  return schedule.tasks
    .filter((t) => t.state === "blocked")
    .map((task) => ({
      task,
      waitingOn: task.blockedBy.map((id) => schedule.byId.get(id)).filter((t): t is ScheduledTask => !!t),
    }))
    .sort((a, b) => a.task.start.localeCompare(b.task.start) || a.task.name.localeCompare(b.task.name));
}

/* ------------------------------------------------------------------ *
 * Plain language (spec §62E)
 * ------------------------------------------------------------------ */

export function describeFloat(totalFloatDays: number): string {
  if (totalFloatDays <= 0) return "No flexibility — delaying this delays the project.";
  return `About ${totalFloatDays} day${totalFloatDays === 1 ? "" : "s"} of flexibility.`;
}

export function describeState(task: ScheduledTask, waitingOn: readonly ScheduledTask[] = []): string {
  switch (task.state) {
    case "complete":
      return "Complete";
    case "in-progress":
      return "In progress";
    case "ready":
      return "Ready to start";
    case "blocked": {
      const names = waitingOn.map((t) => t.name);
      return names.length > 0 ? `Waiting on ${names.join(", ")}` : "Waiting on earlier work";
    }
    case "scheduled":
      return `Scheduled to start ${task.start}`;
  }
}

export { describeDuration };

/**
 * How the project is running against its own plan — only ever from dates
 * that exist. A project with no finished work and no planned dates gets
 * NOTHING rather than a confident zero (spec §62G).
 */
export interface SchedulePerformance {
  /** Working days later than the baseline plan expected; negative = ahead. */
  driftDays: number | null;
  message: string;
}

export function comparePlanned(current: Schedule, planned: Schedule | null): SchedulePerformance {
  if (!planned || !planned.projectFinish || !current.projectFinish) {
    return { driftDays: null, message: "No earlier plan to compare with yet." };
  }
  const drift = workingDaysBetween(planned.projectFinish, current.projectFinish, current.calendar);
  if (drift === 0) return { driftDays: 0, message: "On the plan." };
  return {
    driftDays: drift,
    message:
      drift > 0
        ? `${drift} day${drift === 1 ? "" : "s"} behind the earlier plan.`
        : `${-drift} day${drift === -1 ? "" : "s"} ahead of the earlier plan.`,
  };
}
